import { keys, path } from 'ramda';
import React, { useState } from 'react';
import Marker from 'react-google-maps/lib/components/Marker';
import Polygon from 'react-google-maps/lib/components/Polygon';
import { connect, DispatchProp, useDispatch, useSelector } from 'react-redux';

import InfoBox from 'react-google-maps/lib/components/addons/InfoBox';
import { BaseComponent } from '../../shared/BaseComponent';
import { default as C, UserCan, ACL } from '../../shared/constants';
import {
    alertDotIcon,
    getRadiusHeight,
    getRadiusWidth,
    icoSize,
    shortLocation,
    unitShort,
    userSpeed,
} from '../../shared/helpers';
import { AlertTypes, iDevicePing, iDisplayRegion, iFullStoreState, iLocation, Units } from '../../shared/interfaces';
import { useRedux } from '../../states/redux-state';
import { getMapZoom as getMapZoomFromStore } from '../../stores/reducers/gmap-reducers';
import { getPathsSelector, rightMarkerSelector, topMarkerSelector } from '../../stores/reducers/gmap-selectors';
import { Actions as RegionActions } from '../../stores/reducers/map-region-reducers';
import { removeSelectedPoint, setSelectedPoint } from '../../stores/reducers/selectedPoint/AC';
import { FaVector } from '../elements/fa';
import { MouseTooltip } from '../MouseTooltip';

type IPointProps = {
    point: iDevicePing;
    tripIdx: number;
    color: string;
    icoSizeDiff?: number;
    units: Units;
    isLastPoint: boolean;
    idx: number;
    isDeviceMarkerHidden?: boolean;
}

const anchor = new google.maps.Point(13.5, 13.5);

const getAlertTypeName = (alertActivity: iDevicePing['alertActivity']): string => {
    if (!keys(alertActivity).length) return 'Alert';

    const has = (t: AlertTypes) => alertActivity['has' + t.toLowerCase()];

    const tooltipNames: Partial<Record<AlertTypes, string>> = {
        [AlertTypes.Accel]: 'Acceleration',
        [AlertTypes.Decel]: 'Deceleration',
        [AlertTypes.Heartbeat]: 'Heartbeat',
        [AlertTypes.DriverSmoking]: 'Idle',
        [AlertTypes.Idle]: 'Idle',
        [AlertTypes.IdleStop]: 'Idle Stop',
        [AlertTypes.IdleStart]: 'Idle Start',
        [AlertTypes.IgnOff]: 'Ignition Off',
        [AlertTypes.IgnOn]: 'Ignition On',
        [AlertTypes.InHi]: 'Input High',
        [AlertTypes.InLo]: 'Input Low',
        [AlertTypes.Speed]: 'Speed Alert',
        [AlertTypes.PowerOff]: 'Power Off',
        [AlertTypes.PowerOn]: 'Power On',
        [AlertTypes.SOS]: 'SOS Alert',
        [AlertTypes.TowStart]: 'Tow Start',
        [AlertTypes.TowStop]: 'Tow Stop',
        [AlertTypes.MvStart]: 'Move Start',
        [AlertTypes.MvStop]: 'Move Stop',
        [AlertTypes.TripStart]: 'Trip Start',
        [AlertTypes.TripStop]: 'Trip Stop',
        [AlertTypes.HarshLeftTurn]: 'Harsh Left Turn',
        [AlertTypes.HarshRightTurn]: 'Harsh Right Turn',
        [AlertTypes.FrontCollision]: 'Collision',
        [AlertTypes.UnfastenedSafetyBelt]: 'Seatbelt',
        [AlertTypes.DriverMakingPhoneCalls]: 'Phone Call',
        [AlertTypes.Tailgating]: 'Tailgating',
        [AlertTypes.LensCover]: 'Lens Cover',
        [AlertTypes.StopSignAlarm]: 'Stop Sign',
    };

    const priorityOrder: AlertTypes[] = [
        AlertTypes.Accel,
        AlertTypes.Decel,
        AlertTypes.Heartbeat,
        AlertTypes.DriverSmoking,
        AlertTypes.Idle,
        AlertTypes.IdleStop,
        AlertTypes.IdleStart,
        AlertTypes.IgnOff,
        AlertTypes.IgnOn,
        AlertTypes.InHi,
        AlertTypes.InLo,
        AlertTypes.Speed,
        AlertTypes.PowerOff,
        AlertTypes.PowerOn,
        AlertTypes.SOS,
        AlertTypes.TowStart,
        AlertTypes.TowStop,
        AlertTypes.MvStart,
        AlertTypes.MvStop,
        AlertTypes.TripStart,
        AlertTypes.TripStop,
        AlertTypes.HarshLeftTurn,
        AlertTypes.HarshRightTurn,
        AlertTypes.FrontCollision,
        AlertTypes.UnfastenedSafetyBelt,
        AlertTypes.DriverMakingPhoneCalls,
        AlertTypes.Tailgating,
        AlertTypes.LensCover,
        AlertTypes.StopSignAlarm,
    ];

    const activeAlert = priorityOrder.find(alertType => has(alertType));

    return activeAlert ? (tooltipNames[activeAlert] || 'Alert') : 'Alert';
};

export const Point = React.memo((props: IPointProps) => {
    const dispatch = useDispatch();
    const {point, tripIdx, isLastPoint, icoSizeDiff = 0, color: inColor, units, idx: pointIdx, isDeviceMarkerHidden = false} = props;
    const isHidden = !!point.hidden;

    const {coordinates: {location}} = point;
    const pointLatLng = new google.maps.LatLng(location.lat, location.lng);
    const alertIcon = alertDotIcon(point.alertActivity);

    /* d
    const mapRef = useSelector<iFullStoreState, React.RefObject<GoogleMap> | null>(state => state.gmap.mapRef());

    const outOfBounds = !mapRef?.current || !mapRef.current.getBounds().contains(pointLatLng);

    if (outOfBounds) return null;
    */

    // @ts-ignore
    const isSpeed = point.alertActivity.hasspeed || point.alertActivity.hasspeedposted || point.alertActivity.hasspeedingcapped;
    const color = isSpeed ? '#f00' : inColor;
    const label = isSpeed ? `${userSpeed(point.speed, units)}${unitShort(units)}/h` : `T${tripIdx + 1}-${pointIdx}`;

    // HOVER LOGIC. DO NOT REMOVE IT
    //
    // const removeSelectedPointOnTimeout = (id: string) => {
    //     const selectedPoint = store.getState().selectedPoint;
    //
    //     if (selectedPoint && selectedPoint.pointId === id && !selectedPoint.isClicked) {
    //         clearTimeout(hoverTimeoutId)
    //         setHoverTimeoutId(null);
    //         dispatch(removeSelectedPoint());
    //     }
    // }

    const selectCurrentPoint = () => {
        dispatch(removeSelectedPoint());
        dispatch(setSelectedPoint(point.device, point.tripId, point.pointId, location, true, label));
    };

    // HOVER LOGIC. DO NOT REMOVE IT!
    //
    // const handleMouseEnter = () => {
    //     const selectedPoint = store.getState().selectedPoint;
    //
    //     if (!(selectedPoint && selectedPoint.isClicked === true)) {
    //         dispatch(removeSelectedPoint());
    //         dispatch(setSelectedPoint(point.device, point.tripId, point.pointId, location, false, label));
    //
    //         if (hoverTimeoutId) {
    //             clearTimeout(hoverTimeoutId)
    //             setHoverTimeoutId(null);
    //         }
    //     }
    // };
    //
    // const handleMouseLeave = () => {
    //     const selectedPoint = store.getState().selectedPoint;
    //
    //     const timeoutId = setTimeout(() => {
    //         removeSelectedPointOnTimeout(selectedPoint.pointId);
    //     }, 5000);
    //
    //     setHoverTimeoutId(timeoutId);
    // }
    //
    // useEffect(() => {
    //     return () => {
    //         if (hoverTimeoutId) {
    //             clearTimeout(hoverTimeoutId);
    //         }
    //     };
    // }, [hoverTimeoutId]);

    const isMarkerHidden = isDeviceMarkerHidden === true;

    return (
        <div>
            {/* show the dot or icon */}
            {
                alertIcon && !isMarkerHidden ?
                    <ResizingImage
                      floatRight={isLastPoint}
                      sizeDiff={icoSizeDiff}
                      click={selectCurrentPoint}
                      position={pointLatLng}
                      url={alertIcon}
                      alertActivity={point.alertActivity}
                    /> :
                    !isLastPoint && !isMarkerHidden &&
                 <Marker
                    onClick={selectCurrentPoint}
                    position={pointLatLng}
                    icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: color,
                        fillOpacity: 1,
                        strokeColor: '#000000',
                        strokeOpacity: 1,
                        strokeWeight: 0.25,
                        scale: isLastPoint ? 6 : 4,
                    }}
                    opacity={isHidden ? 0.2 : 1}
                />
            }
        </div>
    );
});

type IResizingImageProps = {
    position: google.maps.LatLngLiteral | google.maps.LatLng;
    url: string;
    click: () => void;
    sizeDiff: number;
    floatRight?: boolean;
    alertActivity?: iDevicePing['alertActivity'];
    onHoverIn?: () => void;
    onHoverOut?: () => void;
}

function ResizingImage(props: IResizingImageProps) {
  const { position, url, click, floatRight = false, alertActivity } = props
  const [showTooltip, setShowTooltip] = useState(false)

  const alertTypeName = alertActivity ? getAlertTypeName(alertActivity) : ''

  const getZoom = useSelector<iFullStoreState, () => number>(
    (state) => () => getMapZoomFromStore(state.gmap)
  )
  const size = getZoom() + props.sizeDiff
  const anchor = floatRight
    ? new google.maps.Point(size / 2 - 25, size / 2 + 25)
    : new google.maps.Point(size / 2, size / 2)

  return (
    <>
      {showTooltip && alertTypeName && (
        <MouseTooltip title={alertTypeName} isVisible={showTooltip} />
      )}
      <Marker
        onClick={floatRight ? () => {} : click}
        position={position}
        zIndex={2}
        opacity={0.5}
        icon={{
          url,
          anchor,
          size: new google.maps.Size(size, size),
          origin: new google.maps.Point(0, 0),
          scaledSize: new google.maps.Size(size, size),
        }}
        onMouseOver={() => setShowTooltip(true)}
        onMouseOut={() => setShowTooltip(false)}
      />
    </>
  )
}

type iOvalPolygonProps = {
    id: string;
}

type iOvalPolygonPropsFromStore = {
    region?: iDisplayRegion;
    getMapZoom: () => number;
    paths: any;
    // calculated
    topHandle?: iLocation;
    rightHandle?: iLocation;
}

type iFullOvalPolygonProps = iOvalPolygonProps & iOvalPolygonPropsFromStore & DispatchProp;

const mapStateToProps = (state: iFullStoreState, ownProps: iOvalPolygonProps): iOvalPolygonPropsFromStore => {
    const getMapZoom = (): number => getMapZoomFromStore(state.gmap);
    try {
        const region = path(['regions', 'vis', ownProps.id], state);
        let ret: iOvalPolygonPropsFromStore = {
            paths: getPathsSelector(state, ownProps),
            region,
            getMapZoom,
        } as any;
        if (ret.region.editable) {
            ret = {
                ...ret,
                topHandle: shortLocation(topMarkerSelector(state, ownProps) as any),
                rightHandle: shortLocation(rightMarkerSelector(state, ownProps) as any),
            };
        }

        return ret;
    } catch (e) {
        return {} as any;
    }

};

type iFullState = {
    isTooltipVisible: boolean;
}

export const OvalPolygon = connect(mapStateToProps)
(class extends BaseComponent<iFullOvalPolygonProps, iFullState> {

    state = {
        isTooltipVisible: false,
        imgSize: 0,
    }

    showTooltip = () => {
        this.setState({isTooltipVisible: true});
    };
    hideTooltip = () => {
        this.setState({isTooltipVisible: false});
    };

    getNameBlock = (lat, lng, title) => {
        return (
            <InfoBox
                options={{
                    disableAutoPan: true,
                    zIndex: 4,
                    boxStyle: {
                        zIndex: 4,
                        padding: '4px 6px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        backgroundColor: '#800',
                        color: '#fff',
                        opacity: '.9',
                        fontSize: '16px',
                    },
                    alignBottom: true,
                    closeBoxURL: '',
                    pixelOffset: new google.maps.Size(45, -12),
                }}
                defaultPosition={new google.maps.LatLng(lat, lng)}
            >
                <div>{title}</div>
            </InfoBox>
        );
    }

    debugRender = () => {
        const {id, rightHandle, dispatch, getMapZoom, topHandle, paths,
            region: {
                name,
                color,
                editable,
                center,
                ico,
                hideIcon,
                hideName,
                hideShadows,
            }} = this.props;

        if (!center) return null;

        let size = icoSize(getMapZoom());

        // ignore hide properties in edit mode;
        const showPolygon = editable || !hideShadows;
        const showIcon = !editable && !hideIcon;
        const showName = name && (editable || !hideName);

        const showTooltip = this.props.region.hideName && !this.props.region.editable;
        const poligonHandlers = showTooltip? {
            onMouseOver: this.showTooltip,
            onMouseOut: this.hideTooltip
        }: {};
        const iconHandlers = showTooltip && hideShadows? {
            onMouseOver: this.showTooltip,
            onMouseOut: this.hideTooltip
        }: {};

        const getImageParams = () => {
            if (ico && !!ico.url) {
                const img = new Image();
                img.src = ico.url;
                const svgSize = 70;
                return {width: !!img.width ? img.width : svgSize};
            }
        }

        const iconSize = size < getImageParams()?.width ? getImageParams().width : size;
        return (
            <div>
                {
                    showTooltip && (
                        <MouseTooltip
                            title={name}
                            isVisible={this.state.isTooltipVisible}
                        />
                    )
                }
                {
                    showPolygon && (
                        <Polygon
                            {...poligonHandlers}
                            paths={paths}
                            options={{strokeWeight: 1.5, strokeColor: color || '#000000', fillColor: color, fillOpacity: C.fenceFillOpacity}}
                        />
                    )
                }

                {/* Top Handle */}
                {!editable ? null :
                    <>
                        <Marker
                            cursor='pointer'
                            icon={{
                                url: 'images/h-bars.png',
                                size: new google.maps.Size(70, 70),
                                origin: new google.maps.Point(0, 0),
                                anchor: new google.maps.Point(size/2, size/2),
                                scaledSize: new google.maps.Size(size, size)
                            }}
                            draggable={true}
                            options={{position: {lat: topHandle!.lat, lng: center.lng}}}
                            onDragEnd={function(x) {
                                // lock it on axis
                                this.setPosition(new google.maps.LatLng(
                                    Math.max(x.latLng.lat(), center.lat),
                                    center.lng
                                ));
                            }}
                            onDrag={function(x) {
                                // lock it on axis
                                this.setPosition(new google.maps.LatLng(
                                    Math.max(x.latLng.lat(), center.lat),
                                    center.lng
                                ));
                                dispatch(RegionActions.UPDATE_REGION_HEIGHT(
                                    id,
                                    getRadiusHeight(center, Math.max(x.latLng.lat(), center.lat))
                                ));
                            }}
                        />
                        {name && this.getNameBlock(center.lat, center.lng, name)}
                    </>
                }
                {
                    // FIX ICO
                    showIcon && ico && (
                        <Marker
                            {...iconHandlers}
                            position={center}
                            icon={{
                            [ico.fa ? 'path' : 'url']: ico.url || FaVector(ico.fa),

                            size: new google.maps.Size(iconSize, iconSize),
                            origin: new google.maps.Point(0, 0),
                            anchor: new google.maps.Point(iconSize/2, iconSize/2),

                            ...(ico.url ? {
                                anchor: new google.maps.Point(iconSize/6, iconSize/6),
                                scaledSize: new google.maps.Size(iconSize/3, iconSize/3),
                            } : {
                                strokeColor: '#000',
                                strokeOpacity: 1,
                                strokeWeight: .25,
                                fillOpacity: .95,
                                fillColor: color,
                                anchor: new google.maps.Point(215, 300),
                                scale: .05 - ((26 - iconSize) / 1000),
                            }),
                            } as any}
                        />
                    )
                }

                {
                    showName && this.getNameBlock(center.lat, center.lng, name)
                }

                {/* Move Handle */}
                {!editable ? null :
                    <>
                        <Marker
                            position={center}
                            draggable
                            onDrag={({latLng}) => { dispatch(RegionActions.UPDATE_REGION_CENTER(id, {lat: latLng.lat(), lng: latLng.lng()}));
                            }}
                            cursor='pointer'
                            icon={{
                                url: 'images/circled_dot.png',
                                size: new google.maps.Size(70, 70),
                                origin: new google.maps.Point(0, 0),
                                anchor: new google.maps.Point(size/2, size/2),
                                scaledSize: new google.maps.Size(size, size)
                            }}
                        />
                        {name && this.getNameBlock(center.lat, center.lng, name)}
                    </>
                }

                {/* Right Handle */}
                {!editable ? null :
                    <Marker
                        position={rightHandle!}
                        icon={{
                            url: 'images/v-bars.png',
                            size: new google.maps.Size(70, 70),
                            origin: new google.maps.Point(0, 0),
                            anchor: new google.maps.Point(size/2, size/2),
                            scaledSize: new google.maps.Size(size, size),
                        }}
                        cursor='pointer'
                        draggable
                        options={{position: {lat: center.lat, lng: rightHandle!.lng}}}
                        onDragEnd={function(x) {
                            // lock it on axis
                            this.setPosition(new google.maps.LatLng(
                                rightHandle!.lat,
                                Math.max(x.latLng.lng(), center.lng)
                            ));
                        }}
                        onDrag={function(x) {
                            // lock it on axis and prevent inverting
                            this.setPosition(new google.maps.LatLng(
                                rightHandle!.lat,
                                Math.max(x.latLng.lng(), center.lng)
                            ));
                            dispatch(RegionActions.UPDATE_REGION_WIDTH(
                                id,
                                getRadiusWidth(center, Math.max(x.latLng.lng(), center.lng))
                            ));
                        }}
                    />
                }
            </div>
        );
    }
});
